import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Database } from "lucide-react";
import { buildAirportFuelQuote } from "../../../domain/rules/airport";
import { eventMatchesAirport } from "../../../domain/rules/events";
import { dayMinuteToLabel } from "../../../domain/rules/schedule";
import type { AirportConfigSet, AirportServiceRecord, FlightInstance, GlobalEvent, SaveGame, WorldData } from "../../../domain/types";
import { PanelHeader } from "../../components/Panel";
import { LedgerRows, formatKg } from "./PlayerShared";
import type { AirportDataObservation, AirportDataSummary, AirportObservationRole } from "./playerTypes";


export function AirportDataView({
  save,
  world,
  airportConfigs,
  activeEvents,
  selectedAirportCode,
  onSelectAirport
}: {
  save: SaveGame;
  world: WorldData;
  airportConfigs: AirportConfigSet;
  activeEvents: GlobalEvent[];
  selectedAirportCode: string;
  onSelectAirport: (airportCode: string) => void;
}) {
  const [query, setQuery] = useState("");
  const opsDayOpen = save.activeOpsDay?.status === "open";
  const records = useMemo(
    () => [...(save.airportServiceRecords ?? [])].sort((a, b) => b.day - a.day || b.minute - a.minute || b.id.localeCompare(a.id)),
    [save.airportServiceRecords]
  );
  const observations = useMemo(() => records.flatMap(airportObservationsForRecord), [records]);
  const airportSummaries = useMemo(() => buildAirportDataSummaries(observations, world, activeEvents, opsDayOpen, airportConfigs), [observations, world, activeEvents, opsDayOpen, airportConfigs]);
  const filteredAirports = airportSummaries.filter((summary) =>
    matchesQuery(
      [
        summary.code,
        airportDisplayName(summary.airport),
        summary.airport?.icao,
        summary.airport?.countryZh,
        summary.latest?.record.flightNo,
        latestServiceStatusSearchText(summary.latest),
        fuelQuoteSearchText(summary.currentFuelQuote),
        currentFuelSourceSearchText(summary.currentFuelSource)
      ],
      query
    )
  );
  const selectedAirport = filteredAirports.find((summary) => summary.code === selectedAirportCode) ?? filteredAirports[0] ?? airportSummaries.find((summary) => summary.code === selectedAirportCode) ?? airportSummaries[0];
  const returnedCount = airportSummaries.filter((summary) => summary.latest).length;
  const delayedCount = airportSummaries.filter((summary) => summary.latest && summary.latest.delayMinutes + summary.latest.fuelQuote.expectedFuelingDelayMinutes > 0).length;
  const fuelObservedAirports = airportSummaries.filter((summary) => summary.currentFuelQuote);
  const averageFuel = averageObservedFuelPrice(fuelObservedAirports);

  useEffect(() => {
    if (!selectedAirport || selectedAirport.code === selectedAirportCode) return;
    const url = new URL(window.location.href);
    url.searchParams.set("view", "airports");
    url.searchParams.set("airport", selectedAirport.code);
    url.searchParams.delete("airportRecord");
    window.history.replaceState(null, "", url);
    onSelectAirport(selectedAirport.code);
  }, [selectedAirport, selectedAirportCode, onSelectAirport]);

  const selectAirport = (airportCode: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", "airports");
    url.searchParams.set("airport", airportCode);
    url.searchParams.delete("airportRecord");
    window.history.replaceState(null, "", url);
    onSelectAirport(airportCode);
  };

  return (
    <section className="player-view airport-data-layout">
      <section className="panel airport-data-list-panel">
        <PanelHeader icon={<Database size={15} />} title="机场数据" code="APT" />
        <div className="airport-data-metrics">
          <div><strong>{airportSummaries.length}</strong><span>机场</span></div>
          <div><strong>{returnedCount}</strong><span>已返回</span></div>
          <div><strong>{delayedCount}</strong><span>等待</span></div>
          <div><strong>{formatAverageFuel(averageFuel)}</strong><span>油价</span></div>
        </div>
        <label className="airport-data-filter">
          筛选
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="机场 / 航班 / 状态" />
        </label>
        <div className="airport-record-timeline">
          {filteredAirports.map((summary) => {
            const latest = summary.latest;
            const currentFuelQuote = summary.currentFuelQuote;
            const delay = airportObservationDelay(latest);
            return (
              <button
                key={summary.code}
                type="button"
                className={airportRecordNodeClassName(summary, selectedAirport)}
                onClick={() => selectAirport(summary.code)}
              >
                <span className="airport-record-time">
                  {airportRecordTimeLabel(summary, latest, currentFuelQuote)}
                </span>
                <strong>{summary.code} {airportDisplayName(summary.airport)}</strong>
                <em>
                  {airportRecordStatusLine(summary, latest, currentFuelQuote)}
                  {airportDelaySuffix(delay)}
                </em>
              </button>
            );
          })}
          {filteredAirports.length === 0 && <div className="empty-state">没有匹配的机场数据记录。</div>}
        </div>
      </section>

      <section className="panel airport-data-detail-panel">
        {renderAirportRecordDetail(selectedAirport, opsDayOpen)}
      </section>
    </section>
  );
}

type CurrentFuelQuote = AirportDataSummary["currentFuelQuote"];
type AirportOfferSnapshot = NonNullable<AirportServiceRecord["offer"]>;

function latestServiceStatusSearchText(latest: AirportDataObservation | undefined): string {
  if (latest) {
    return formatAirportServiceStatus(latest.status);
  }
  return "暂无返回";
}

function fuelQuoteSearchText(fuelQuote: CurrentFuelQuote): string | undefined {
  if (fuelQuote) {
    return formatFuelQuote(fuelQuote);
  }
  return undefined;
}

function currentFuelSourceSearchText(source: AirportDataSummary["currentFuelSource"]): string | undefined {
  if (source === "ops_config") {
    return "运行日配置";
  }
  return undefined;
}

function averageObservedFuelPrice(summaries: AirportDataSummary[]): number {
  if (summaries.length === 0) {
    return 0;
  }
  const totalFuelPrice = summaries.reduce((sum, summary) => sum + (summary.currentFuelQuote?.priceUsdPerTon ?? 0), 0);
  return Math.round(totalFuelPrice / summaries.length);
}

function airportObservationDelay(latest: AirportDataObservation | undefined): number {
  if (!latest) {
    return 0;
  }
  return latest.delayMinutes + latest.fuelQuote.expectedFuelingDelayMinutes;
}

function airportRecordNodeClassName(summary: AirportDataSummary, selectedAirport: AirportDataSummary | undefined): string {
  const classes = ["airport-record-node"];
  if (summary.code === selectedAirport?.code) {
    classes.push("active");
  }
  classes.push(summary.latest?.record.decision ?? "empty");
  return classes.join(" ");
}

function airportRecordTimeLabel(summary: AirportDataSummary, latest: AirportDataObservation | undefined, currentFuelQuote: CurrentFuelQuote): string {
  if (latest) {
    return `D${latest.record.day} / ${dayMinuteToLabel(latest.record.minute)} / ${summary.observations.length} 条`;
  }
  const fallbackAirportCode = summary.airport?.icao ?? summary.code;
  return `${fallbackAirportCode} / ${airportCurrentFuelLabel(currentFuelQuote)}`;
}

function airportCurrentFuelLabel(currentFuelQuote: CurrentFuelQuote): string {
  if (currentFuelQuote) {
    return "运行日配置";
  }
  return "暂无返回";
}

function airportRecordStatusLine(summary: AirportDataSummary, latest: AirportDataObservation | undefined, currentFuelQuote: CurrentFuelQuote): string {
  if (latest) {
    return `${formatAirportServiceStatus(latest.status)} / ${formatFuelQuoteOr(currentFuelQuote, "--")}`;
  }
  return `${summary.airport?.countryZh ?? "--"} / ${formatFuelQuoteOr(currentFuelQuote, "等待机场返回")}`;
}

function airportDelaySuffix(delayMinutes: number): string {
  if (delayMinutes > 0) {
    return ` / 延误 ${delayMinutes}m`;
  }
  return "";
}

function renderAirportRecordDetail(selectedAirport: AirportDataSummary | undefined, opsDayOpen: boolean): ReactNode {
  if (selectedAirport) {
    return <AirportRecordDetail summary={selectedAirport} opsDayOpen={opsDayOpen}/>;
  }
  return <div className="empty-state">航班放行后会保存机场返回的数据快照。</div>;
}

function AirportRecordDetail({ summary, opsDayOpen }: { summary: AirportDataSummary; opsDayOpen: boolean }) {
  const latest = summary.latest;
  const latestOrigin = summary.latestOrigin;
  const latestRecord = latest?.record;
  const offer = latestRecord?.offer;
  const currentFuelQuote = summary.currentFuelQuote;
  const totalDelay = airportObservationDelay(latest);
  const eventSnapshots = airportSummaryEvents(summary);
  return (
    <div className="airport-record-detail">
      <div className="airport-record-hero">
        <div>
          <span>{airportHeroTimestampLabel(latestRecord, opsDayOpen, currentFuelQuote)}</span>
          <h3>{summary.code} {airportDisplayName(summary.airport)}</h3>
          <p>
            {airportHeroDescription(summary, latest, latestRecord, currentFuelQuote)}
          </p>
        </div>
        <strong>{formatFuelQuoteOr(currentFuelQuote, "--")}</strong>
      </div>

      <div className="airport-record-grid">
        <section className="ledger-card">
          <header>
            <span>出港业载</span>
            <em>{latestOriginPayloadLabel(latestOrigin)}</em>
          </header>
          {renderLatestOriginLedger(latestOrigin)}
        </section>
        <section className="ledger-card">
          <header>
            <span>燃油</span>
            <em>{formatFuelQuoteOr(currentFuelQuote, "暂无")}</em>
          </header>
          {renderFuelLedger(currentFuelQuote, summary.currentFuelSource, latest, latestRecord)}
        </section>
        <section className="ledger-card">
          <header>
            <span>{handlingSectionTitle(latest)}</span>
            <em>{airportHandlingDelayLabel(latest, totalDelay)}</em>
          </header>
          {renderHandlingLedger(offer, summary)}
        </section>
        <section className="ledger-card">
          <header>
            <span>关联返回</span>
            <em>{summary.observations.length} 条</em>
          </header>
          {renderObservationList(summary.observations)}
        </section>
      </div>

      <section className="airport-event-strip">
        <strong>事件叠加</strong>
        {renderAirportEventSnapshots(eventSnapshots)}
      </section>
    </div>
  );
}

function airportHeroTimestampLabel(latestRecord: AirportServiceRecord | undefined, opsDayOpen: boolean, currentFuelQuote: CurrentFuelQuote): string {
  if (latestRecord) {
    return `D${latestRecord.day} / ${dayMinuteToLabel(latestRecord.minute)} / 最近返回`;
  }
  if (opsDayOpen && currentFuelQuote) {
    return "运行日配置 / 当前油价";
  }
  return "暂无机场返回";
}

function latestOriginPayloadLabel(latestOrigin: AirportDataObservation | undefined): string {
  if (latestOrigin) {
    return `${latestOrigin.passengers} pax / ${formatKg(latestOrigin.cargoKg)}`;
  }
  return "暂无";
}

function renderLatestOriginLedger(latestOrigin: AirportDataObservation | undefined): ReactNode {
  if (!latestOrigin) {
    return <div className="ledger-empty">暂无本站出港业载返回。</div>;
  }
  return (
    <LedgerRows rows={[
      ["航班", `${latestOrigin.record.flightNo} ${latestOrigin.record.origin}-${latestOrigin.record.destination}`],
      ["旅客", `${latestOrigin.passengers} pax`],
      ["行李", formatKg(latestOrigin.record.offer.payloadOffer.baggageKg)],
      ["货邮", formatKg(latestOrigin.cargoKg)],
      ["收益率", `$${latestOrigin.record.offer.payloadOffer.yieldUsdPerKm.toFixed(3)}/km`],
      ["依据", latestOrigin.record.offer.payloadOffer.reasons.join("；")]
    ]}/>
  );
}

function renderFuelLedger(
  currentFuelQuote: CurrentFuelQuote,
  source: AirportDataSummary["currentFuelSource"],
  latest: AirportDataObservation | undefined,
  latestRecord: AirportServiceRecord | undefined
): ReactNode {
  if (!currentFuelQuote) {
    return <div className="ledger-empty">暂无本站燃油返回。</div>;
  }
  return (
    <LedgerRows rows={[
      ["来源", fuelSourceLabel(source, latest, latestRecord)],
      ["油价", `$${currentFuelQuote.priceUsdPerTon}/t`],
      ["供油", fuelAvailabilityLabel(currentFuelQuote.available)],
      ["加油上限", formatKg(currentFuelQuote.maxUpliftKg)],
      ["加油速率", `${Math.round(currentFuelQuote.refuelRateKgPerMin)} kg/min`],
      ["加油等待", `${currentFuelQuote.expectedFuelingDelayMinutes}m`],
      ["依据", currentFuelQuote.reasons.join("；")]
    ]}/>
  );
}

function fuelAvailabilityLabel(available: boolean): string {
  if (available) {
    return "可供油";
  }
  return "停供";
}

function handlingSectionTitle(latest: AirportDataObservation | undefined): string {
  if (latest) {
    return "运行保障";
  }
  return "机场资料";
}

function renderHandlingLedger(offer: AirportOfferSnapshot | undefined, summary: AirportDataSummary): ReactNode {
  if (offer) {
    return (
      <LedgerRows rows={[
        ["容量状态", formatAirportServiceStatus(offer.handling.status)],
        ["地面延误", `${offer.handling.expectedGroundDelayMinutes}m`],
        ["阻断", offer.handling.blockers.join("；") || "无"],
        ["依据", offer.handling.reasons.join("；")]
      ]}/>
    );
  }
  return (
    <LedgerRows rows={[
      ["ICAO", summary.airport?.icao ?? "--"],
      ["城市", summary.airport?.cityZh ?? "--"],
      ["时区", summary.airport?.timeZone ?? "--"],
      ["标高", airportElevation(summary.airport)]
    ]}/>
  );
}

function renderObservationList(observations: AirportDataObservation[]): ReactNode {
  if (observations.length === 0) {
    return <div className="ledger-empty">暂无航班使用过该机场返回数据。</div>;
  }
  return (
    <div className="airport-observation-list">
      {observations.slice(0, 8).map((observation) => (
        <span key={observation.key}>
          D{observation.record.day} {dayMinuteToLabel(observation.record.minute)} / {formatAirportRole(observation.role)} / {observation.record.flightNo} {observation.record.origin}-{observation.record.destination}
        </span>
      ))}
    </div>
  );
}

function renderAirportEventSnapshots(eventSnapshots: AirportServiceRecord["eventSnapshots"]): ReactNode {
  if (eventSnapshots.length === 0) {
    return <em>无活动事件影响</em>;
  }
  return (
    <div>
      {eventSnapshots.map((event) => (
        <span key={event.id}>
          {event.title}{eventReasonSuffix(event.reason)}
        </span>
      ))}
    </div>
  );
}

function eventReasonSuffix(reason: string | undefined): string {
  if (reason) {
    return ` / ${reason}`;
  }
  return "";
}

function airportObservationsForRecord(record: AirportServiceRecord): AirportDataObservation[] {
  const baseDelay = record.summary.expectedGroundDelayMinutes;
  const originBlockers = record.summary.blockers.filter((blocker) => blocker.includes(record.origin));
  const destinationBlockers = record.summary.blockers.filter((blocker) => blocker.includes(record.destination));
  return [
    {
      key: `${record.id}:origin`,
      record,
      role: "origin",
      airportCode: record.origin,
      counterpartCode: record.destination,
      fuelQuote: record.offer.fuelQuote,
      status: record.summary.status,
      passengers: record.summary.passengers,
      cargoKg: record.summary.cargoKg,
      delayMinutes: baseDelay,
      blockers: airportRoleBlockers(originBlockers, record.summary.blockers)
    },
    {
      key: `${record.id}:destination`,
      record,
      role: "destination",
      airportCode: record.destination,
      counterpartCode: record.origin,
      fuelQuote: record.offer.destinationFuelQuote,
      status: record.summary.status,
      passengers: record.summary.passengers,
      cargoKg: record.summary.cargoKg,
      delayMinutes: baseDelay,
      blockers: airportRoleBlockers(destinationBlockers, record.summary.blockers)
    }
  ];
}

function airportRoleBlockers(roleBlockers: string[], summaryBlockers: string[]): string[] {
  if (roleBlockers.length > 0) {
    return roleBlockers;
  }
  return summaryBlockers;
}

export function buildAirportDataSummaries(observations: AirportDataObservation[], world: WorldData, activeEvents: GlobalEvent[] = [], includeConfiguredFuel = false, airportConfigs?: AirportConfigSet): AirportDataSummary[] {
  const groups = new Map<string, AirportDataObservation[]>();
  for (const observation of observations) {
    groups.set(observation.airportCode, [...(groups.get(observation.airportCode) ?? []), observation]);
  }
  const summaries: AirportDataSummary[] = [];
  for (const airport of world.airports) {
    const group = groups.get(airport.code) ?? [];
    const sorted = [...group].sort(sortAirportObservations);
    const latest = sorted[0];
    const configuredFuelQuote = configuredAirportFuelQuote(includeConfiguredFuel, airport, activeEvents, airportConfigs);
    summaries.push({
      code: airport.code,
      airport,
      observations: sorted,
      latest,
      latestOrigin: sorted.find((observation) => observation.role === "origin"),
      currentFuelQuote: configuredFuelQuote ?? latest?.fuelQuote,
      currentFuelSource: airportCurrentFuelSource(configuredFuelQuote, latest),
      currentEventSnapshots: configuredAirportEvents(includeConfiguredFuel, airport, activeEvents)
    });
    groups.delete(airport.code);
  }
  for (const [code, group] of groups.entries()) {
    const sorted = [...group].sort(sortAirportObservations);
    summaries.push({
      code,
      airport: undefined,
      observations: sorted,
      latest: sorted[0],
      latestOrigin: sorted.find((observation) => observation.role === "origin"),
      currentFuelQuote: sorted[0]?.fuelQuote,
      currentFuelSource: serviceRecordFuelSource(sorted[0]),
      currentEventSnapshots: []
    });
  }
  return summaries.sort(sortAirportSummaries);
}

function configuredAirportFuelQuote(
  includeConfiguredFuel: boolean,
  airport: WorldData["airports"][number],
  activeEvents: GlobalEvent[],
  airportConfigs: AirportConfigSet | undefined
): CurrentFuelQuote {
  if (includeConfiguredFuel) {
    const fuelConfig = airportConfigs?.fuel.find((config) => config.airportCode === airport.code);
    if (fuelConfig) {
      return buildAirportFuelQuote(airport, activeEvents, fuelConfig);
    }
  }
  return undefined;
}

function configuredAirportEvents(includeConfiguredFuel: boolean, airport: WorldData["airports"][number], activeEvents: GlobalEvent[]): AirportServiceRecord["eventSnapshots"] {
  if (!includeConfiguredFuel) {
    return [];
  }
  return activeEvents.filter((event) => eventMatchesAirport(event, airport)).map(eventSnapshotFromGlobalEvent);
}

function serviceRecordFuelSource(latest: AirportDataObservation | undefined): AirportDataSummary["currentFuelSource"] {
  if (latest) {
    return "service_record";
  }
  return undefined;
}

function sortAirportSummaries(a: AirportDataSummary, b: AirportDataSummary): number {
  if (a.latest && b.latest) {
    return sortAirportObservations(a.latest, b.latest);
  }
  if (a.latest) return -1;
  if (b.latest) return 1;
  return a.code.localeCompare(b.code);
}

function sortAirportObservations(a: AirportDataObservation, b: AirportDataObservation): number {
  return b.record.day - a.record.day || b.record.minute - a.record.minute || a.airportCode.localeCompare(b.airportCode) || a.key.localeCompare(b.key);
}

function airportSummaryEvents(summary: AirportDataSummary): AirportServiceRecord["eventSnapshots"] {
  const seen = new Set<string>();
  const events: AirportServiceRecord["eventSnapshots"] = [];
  for (const event of summary.currentEventSnapshots) {
    if (seen.has(event.id)) continue;
    seen.add(event.id);
    events.push(event);
  }
  for (const observation of summary.observations) {
    for (const event of observation.record.eventSnapshots) {
      if (seen.has(event.id)) continue;
      seen.add(event.id);
      events.push(event);
    }
  }
  return events;
}

function formatAirportServiceStatus(status: NonNullable<FlightInstance["airportOfferSnapshot"]>["handling"]["status"]): string {
  if (status === "open") return "开放";
  if (status === "restricted") return "受限";
  return "关闭";
}

function formatAirportRole(role: AirportObservationRole): string {
  if (role === "origin") {
    return "出港";
  }
  return "到港";
}

function airportDisplayName(airport: WorldData["airports"][number] | undefined): string {
  if (airport) {
    return `${airport.cityZh}${airport.nameZh}`;
  }
  return "--";
}

function formatFuelQuote(fuelQuote: NonNullable<AirportDataSummary["currentFuelQuote"]>): string {
  if (fuelQuote.available) {
    return `$${fuelQuote.priceUsdPerTon}/t`;
  }
  return `停供 / $${fuelQuote.priceUsdPerTon}/t`;
}

function formatAverageFuel(value: number): string {
  if (value === 0) {
    return "--";
  }
  return `$${value}/t`;
}

function formatFuelQuoteOr(fuelQuote: AirportDataSummary["currentFuelQuote"], fallback: string): string {
  if (fuelQuote) {
    return formatFuelQuote(fuelQuote);
  }
  return fallback;
}

function airportHandlingDelayLabel(latest: AirportDataObservation | undefined, totalDelay: number): string {
  if (!latest) {
    return "暂无";
  }
  if (totalDelay > 0) {
    return `延误 ${totalDelay}m`;
  }
  return "正常";
}

function airportCurrentFuelSource(
  configuredFuelQuote: AirportDataSummary["currentFuelQuote"],
  latest: AirportDataObservation | undefined
): AirportDataSummary["currentFuelSource"] {
  if (configuredFuelQuote) {
    return "ops_config";
  }
  if (latest) {
    return "service_record";
  }
  return undefined;
}

function airportHeroDescription(
  summary: AirportDataSummary,
  latest: AirportDataObservation | undefined,
  latestRecord: AirportServiceRecord | undefined,
  currentFuelQuote: AirportDataSummary["currentFuelQuote"]
): string {
  if (latest && latestRecord) {
    return `${formatAirportServiceStatus(latest.status)} / ${formatAirportRole(latest.role)} / 关联 ${latestRecord.flightNo} ${latestRecord.origin}-${latestRecord.destination}`;
  }
  let fuelStatus = "等待机场返回运行数据";
  if (currentFuelQuote) {
    fuelStatus = "运行日燃油配置已发布";
  }
  return `${summary.airport?.countryZh ?? "--"} / ${summary.airport?.icao ?? summary.code} / ${fuelStatus}`;
}

function fuelSourceLabel(
  source: AirportDataSummary["currentFuelSource"],
  latest: AirportDataObservation | undefined,
  latestRecord: AirportServiceRecord | undefined
): string {
  if (source === "ops_config") {
    if (latestRecord) {
      return `运行日配置 / 最近返回 ${latestRecord.flightNo}`;
    }
    return "运行日配置";
  }
  if (latest && latestRecord) {
    return `${latestRecord.flightNo} / ${formatAirportRole(latest.role)}`;
  }
  return "机场配置";
}

function airportElevation(airport: WorldData["airports"][number] | undefined): string {
  if (airport) {
    return `${airport.elevationFt} ft`;
  }
  return "--";
}

function eventSnapshotFromGlobalEvent(event: GlobalEvent): AirportServiceRecord["eventSnapshots"][number] {
  return {
    id: event.id,
    title: event.title,
    severity: event.severity,
    scopeType: event.scopeType,
    scopeValue: event.scopeValue,
    reason: event.reason,
    effectTypes: event.effects.map((effect) => effect.type)
  };
}

function matchesQuery(values: Array<string | number | undefined>, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return values.some((value) => String(value ?? "").toLowerCase().includes(needle));
}
